import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "../lib/api";
import {
  format, subDays, startOfWeek, endOfWeek,
  startOfMonth, endOfMonth, subWeeks, subMonths,
} from "date-fns";
import { es } from "date-fns/locale";
import {
  TrendingUp, Calendar, ArrowUpDown, ArrowUp, ArrowDown,
  Search, Settings2, Copy, Check, Download, Info, AlertTriangle, RefreshCw,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import DashboardLayout from "../components/DashboardLayout";
import { toast } from "sonner";
import { API } from "../App";
import { getActiveOrgId } from "../lib/api";

// ─── Constants ────────────────────────────────────────────────────────────────
const DEFAULT_SETTINGS = {
  umbral_escalar: 20, umbral_mantener: 10, umbral_minimo: 0,
  umbral_halo_proteger: 1.3, umbral_margen_alto: 30, umbral_margen_medio: 15,
  factor_minimo: 0.2, dias_lookback: 30, hora_regeneracion: 6,
};

// ─── Formatters ───────────────────────────────────────────────────────────────
const isNeg  = (v) => typeof v === "number" && v < 0;
const fmtEur = (v) => `€${(v || 0).toLocaleString("es-ES", { minimumFractionDigits: 2 })}`;
const fmtPct = (v) => v == null ? "—" : `${Number(v).toLocaleString("es-ES", { minimumFractionDigits: 2 })}%`;
const fmtX   = (v) => v == null ? "—" : `${Number(v).toLocaleString("es-ES", { minimumFractionDigits: 2 })}x`;
const fmtInt = (v) => (v || 0).toLocaleString("es-ES");

// ─── Decision logic ───────────────────────────────────────────────────────────
function calcDecision(row, settings) {
  const spend = row.gads_spend || 0;
  const rent  = row.rent_real_pct;
  const halo  = row.halo_score;
  const { umbral_escalar, umbral_mantener, umbral_halo_proteger } = settings;

  if (!spend || spend <= 0)           return { key: "sin_publicidad", label: "Sin publicidad", color: "gray" };

  // rent_real: si no hay ingresos, considerar -100% (todo gasto sin retorno)
  const effectiveRent = (rent == null || row.direct_revenue <= 0) ? -100 : rent;

  if (effectiveRent >= umbral_escalar)  return { key: "escalar",     label: "Escalar",     color: "green" };
  if (effectiveRent >= umbral_mantener) return { key: "mantener",    label: "Mantener",    color: "blue" };
  if (effectiveRent >= 0)               return { key: "reducir_bid", label: "Reducir bid", color: "orange" };
  if (halo != null && halo >= umbral_halo_proteger)
    return { key: "mantener", label: "Mantener", color: "blue", isAnchor: true };
  return { key: "pausar", label: "Pausar", color: "red" };
}

const CHIP_CLR = {
  green:  "bg-green-100 text-green-800 border border-green-200",
  blue:   "bg-blue-100  text-blue-800  border border-blue-200",
  orange: "bg-orange-100 text-orange-800 border border-orange-200",
  red:    "bg-red-100   text-red-800   border border-red-200",
  gray:   "bg-slate-100 text-slate-500  border border-slate-200",
};

function DecisionChip({ d }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${CHIP_CLR[d.color]}`}
      title={d.isAnchor ? "Producto ancla: rentabilidad negativa pero Halo Score significativo" : undefined}
      data-testid="decision-chip"
    >
      {d.isAnchor && <span>⚓</span>}
      {d.label}
    </span>
  );
}

// ─── Sort helpers ─────────────────────────────────────────────────────────────
const SORT_FIELDS = new Set([
  "reference", "name", "direct_conversions", "direct_revenue", "direct_cost",
  "gads_spend", "roas_gads", "rent_gads_pct", "cross_revenue", "halo_score", "rent_real_pct",
]);

function getInitialDate(s) {
  const p = new URLSearchParams(s);
  if (p.get("preset") === "last30") return { from: subDays(new Date(), 29), to: new Date() };
  return { from: null, to: null };
}

const FILTER_CHIPS = [
  { key: "all",            label: "Todos",          dot: "bg-slate-400" },
  { key: "escalar",        label: "Escalar",        dot: "bg-green-500" },
  { key: "mantener",       label: "Mantener",       dot: "bg-blue-500" },
  { key: "reducir_bid",    label: "Reducir bid",    dot: "bg-orange-500" },
  { key: "pausar",         label: "Pausar",         dot: "bg-red-500" },
  { key: "sin_publicidad", label: "Sin publicidad", dot: "bg-slate-300" },
];

const COLUMNS = [
  { key: "reference",           label: "Referencia",           align: "left",  sortable: true },
  { key: "name",                label: "Nombre",               align: "left",  sortable: true },
  { key: "direct_conversions",  label: "Conv. Directas",       align: "right", sortable: true },
  { key: "direct_revenue",      label: "Ingresos Directos",    align: "right", sortable: true },
  { key: "direct_cost",         label: "Coste Directo",        align: "right", sortable: true },
  { key: "gads_spend",          label: "Gasto GAds",           align: "right", sortable: true },
  { key: "roas_gads",           label: "ROAS GAds",            align: "right", sortable: true },
  { key: "rent_gads_pct",       label: "Rentabilidad GAds %",  align: "right", sortable: true },
  { key: "cross_revenue",       label: "Ing. Cruzados",        align: "right", sortable: true },
  { key: "halo_score",          label: "Halo Score Google",    align: "right", sortable: true },
  { key: "_rent_real",          label: "Rentabilidad Real %",  align: "right", special: true },
  { key: "_decision",           label: "Decisión",             align: "center", special: true },
];

// ─── Component ────────────────────────────────────────────────────────────────
export default function GoogleAdsProductsPage() {
  const location = useLocation();
  const [dateRange, setDateRange]     = useState(() => getInitialDate(location.search));
  const [pendingDate, setPendingDate] = useState(() => getInitialDate(location.search));
  const [calOpen, setCalOpen]         = useState(false);
  const [search, setSearch]           = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [rows, setRows]               = useState([]);
  const [noUtmData, setNoUtmData]     = useState(false);
  const [pagination, setPagination]   = useState({ page: 1, page_size: 50, total_rows: 0, total_pages: 1 });
  const [sort, setSort]               = useState({ sortBy: "direct_revenue", sortOrder: "desc" });
  const [loading, setLoading]         = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");
  const [dataSource, setDataSource]   = useState(null); // "pixel" | "ga4" | null
  const [decisionCounts, setDecisionCounts] = useState({ escalar: 0, mantener: 0, reducir_bid: 0, pausar: 0, sin_publicidad: 0 });

  // Feed Optimizer state
  const [feedSettings, setFeedSettings]     = useState(DEFAULT_SETTINGS);
  const [feedToken, setFeedToken]           = useState(null);
  const [drawerOpen, setDrawerOpen]         = useState(false);
  const [drawerSettings, setDrawerSettings] = useState(DEFAULT_SETTINGS);
  const [drawerSaving, setDrawerSaving]     = useState(false);
  const [feedCopied, setFeedCopied]         = useState(false);
  const [feedStats, setFeedStats]           = useState(null);
  const [feedGenerating, setFeedGenerating] = useState(false);
  const feedPollRef = useRef(null);

  const hasRange = Boolean(dateRange.from && dateRange.to);

  const presets = useMemo(() => [
    { label: "Últimos 7 días",  from: subDays(new Date(), 6),                           to: new Date() },
    { label: "Últimos 30 días", from: subDays(new Date(), 29),                          to: new Date() },
    { label: "Este mes",        from: startOfMonth(new Date()),                         to: new Date() },
    { label: "Mes pasado",      from: startOfMonth(subMonths(new Date(), 1)),           to: endOfMonth(subMonths(new Date(), 1)) },
    { label: "Esta semana",     from: startOfWeek(new Date(), { weekStartsOn: 1 }),     to: new Date() },
    { label: "Semana pasada",   from: startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }), to: endOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }) },
  ], []);

  // Enrich rows with decision
  const enrichedRows = useMemo(() => rows.map((row) => {
    const decision = calcDecision(row, feedSettings);
    return { ...row, _decision: decision };
  }), [rows, feedSettings]);

  // filteredRows is now just enrichedRows — filtering is done server-side
  const filteredRows = enrichedRows;

  // counts come from backend (over all products, not just current page)
  const counts = decisionCounts;

  const previewRows = useMemo(() => enrichedRows.slice(0, 10), [enrichedRows]);
  const feedUrl = feedToken ? `${API}/public/merchant-feed/${feedToken}/supplemental.csv` : null;

  // Load feed settings
  const loadSettings = useCallback(async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const orgId = getActiveOrgId();
      const res = await axios.get(`${API}/feed-optimizer/settings`, {
        withCredentials: true,
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(orgId ? { "X-Org-Id": orgId } : {}),
        },
      });
      setFeedSettings(res.data.settings || DEFAULT_SETTINGS);
      setDrawerSettings(res.data.settings || DEFAULT_SETTINGS);
      setFeedToken(res.data.merchant_feed_token || null);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  // Debounce search to avoid firing on every keystroke
  useEffect(() => {
    const t = setTimeout(() => { setPagination((p) => ({ ...p, page: 1 })); setDebouncedSearch(search); }, 500);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setPagination((p) => ({ ...p, page: 1 }));
  }, [activeFilter]);

  // Fetch data from new UTM-based endpoint
  const fetchData = async (page = pagination.page) => {
    if (!dateRange.from || !dateRange.to) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const orgId = getActiveOrgId();
      const res = await axios.get(`${API}/analytics/google-ads-products-utm`, {
        params: {
          start_date: format(dateRange.from, "yyyy-MM-dd"),
          end_date:   format(dateRange.to,   "yyyy-MM-dd"),
          search:     debouncedSearch.trim() || undefined,
          sort_by:    SORT_FIELDS.has(sort.sortBy) ? sort.sortBy : "direct_revenue",
          sort_order: sort.sortOrder,
          decision_filter: activeFilter !== "all" ? activeFilter : undefined,
          page,
          page_size: 50,
        },
        withCredentials: true,
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(orgId ? { "X-Org-Id": orgId } : {}),
        },
      });
      setNoUtmData(!!res.data.no_utm_data);
      setDataSource(res.data.source || null);
      setRows(res.data.rows || []);
      if (res.data.decision_counts) setDecisionCounts(res.data.decision_counts);
      setPagination(res.data.pagination || { page: 1, page_size: 50, total_rows: 0, total_pages: 1 });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error cargando datos");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (hasRange) fetchData(1); }, [hasRange, dateRange, debouncedSearch, activeFilter]);
  useEffect(() => { if (hasRange) fetchData(pagination.page); }, [sort]);

  const onSort = (key) => {
    setSort((s) => s.sortBy === key
      ? { ...s, sortOrder: s.sortOrder === "asc" ? "desc" : "asc" }
      : { sortBy: key, sortOrder: "desc" });
  };
  const SortIcon = ({ k }) => {
    if (sort.sortBy !== k) return <ArrowUpDown className="w-3 h-3" />;
    return sort.sortOrder === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />;
  };

  // Feed Optimizer handlers
  const handleCopy = () => {
    if (!feedUrl) return;
    navigator.clipboard.writeText(feedUrl).then(() => {
      setFeedCopied(true); toast.success("URL copiada");
      setTimeout(() => setFeedCopied(false), 2500);
    });
  };

  const handleSave = async () => {
    setDrawerSaving(true);
    try {
      const token = localStorage.getItem("auth_token");
      const orgId = getActiveOrgId();
      const res = await axios.put(`${API}/feed-optimizer/settings`, drawerSettings, {
        withCredentials: true,
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(orgId ? { "X-Org-Id": orgId } : {}),
        },
      });
      const s = res.data.settings;
      setFeedSettings(s); setDrawerSettings(s);
      toast.info("Configuración guardada. Regenerando CSV...");
      // Start polling until CSV is ready
      startFeedPolling();
    } catch { toast.error("Error guardando"); }
    finally { setDrawerSaving(false); }
  };

  const handleRegenerate = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const orgId = getActiveOrgId();
      await axios.post(`${API}/feed-optimizer/refresh-stats`, {}, {
        withCredentials: true,
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(orgId ? { "X-Org-Id": orgId } : {}),
        },
      });
      toast.info("Regenerando CSV...");
      startFeedPolling();
    } catch { toast.error("Error iniciando regeneración"); }
  };

  const handleDownload = () => {
    if (!feedUrl) return;
    const a = document.createElement("a");
    a.href = feedUrl; a.download = "supplemental_feed.csv"; a.click();
  };

  const fetchFeedStats = useCallback(async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const orgId = getActiveOrgId();
      const res = await axios.get(`${API}/feed-optimizer/preview-stats`, {
        withCredentials: true,
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(orgId ? { "X-Org-Id": orgId } : {}),
        },
      });
      setFeedStats(res.data);
      return res.data;
    } catch { setFeedStats(null); return null; }
  }, []);

  // Poll preview stats every 5s until CSV is ready (max 2 min)
  const startFeedPolling = useCallback(() => {
    if (feedPollRef.current) clearInterval(feedPollRef.current);
    setFeedGenerating(true);
    let attempts = 0;
    feedPollRef.current = setInterval(async () => {
      attempts++;
      const token = localStorage.getItem("auth_token");
      const orgId = getActiveOrgId();
      try {
        const res = await axios.get(`${API}/feed-optimizer/preview-stats`, {
          withCredentials: true,
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(orgId ? { "X-Org-Id": orgId } : {}),
          },
        });
        const data = res.data;
        setFeedStats(data);
        const ready = data.total_products > 0 && data.status !== "generating";
        if (ready || attempts >= 24) {
          clearInterval(feedPollRef.current);
          feedPollRef.current = null;
          setFeedGenerating(false);
          if (ready) toast.success(`Feed listo: ${data.total_products.toLocaleString("es-ES")} productos`);
        }
      } catch {
        if (attempts >= 24) {
          clearInterval(feedPollRef.current);
          feedPollRef.current = null;
          setFeedGenerating(false);
        }
      }
    }, 5000);
  }, []);

  // Cleanup on unmount
  useEffect(() => () => { if (feedPollRef.current) clearInterval(feedPollRef.current); }, []);

  useEffect(() => {
    if (drawerOpen) {
      fetchFeedStats().then((data) => {
        if (data && (data.status === "generating" || data.total_products === 0)) {
          startFeedPolling();
        }
      });
    }
  }, [drawerOpen, fetchFeedStats]);

  const canPrev = pagination.page > 1;
  const canNext = pagination.page < pagination.total_pages;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="google-ads-products-page">

        {/* Header */}
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-black tracking-tight text-slate-900">Google Ads — Productos</h1>
              <p className="text-sm text-slate-500">Atribución por UTM desde pedidos reales · Feed Optimizer para Merchant Center</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" asChild><Link to="/analytics">GA4</Link></Button>
            <Button variant="outline" asChild><Link to="/analytics/products">Productos</Link></Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" asChild>
              <Link to="/analytics/google-ads-products">GAds — Productos</Link>
            </Button>
          </div>
        </div>

        {/* Controls */}
        <Card className="bg-white border border-slate-200">
          <CardContent className="p-4 flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
            <Popover open={calOpen} onOpenChange={(o) => { setCalOpen(o); if (o) setPendingDate(dateRange); }}>
              <PopoverTrigger asChild>
                <Button variant="outline" data-testid="gads-products-date-picker">
                  <Calendar className="w-4 h-4 mr-2" />
                  {hasRange
                    ? `${format(dateRange.from, "d MMM", { locale: es })} – ${format(dateRange.to, "d MMM yyyy", { locale: es })}`
                    : "Selecciona rango de fechas"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <div className="flex">
                  <div className="border-r border-slate-200 p-2 space-y-1 min-w-[170px]">
                    <p className="text-xs font-semibold text-slate-500 uppercase px-2 py-1">Accesos rápidos</p>
                    {presets.map((p) => (
                      <button key={p.label}
                        onClick={() => { const r = { from: p.from, to: p.to }; setDateRange(r); setPendingDate(r); setCalOpen(false); }}
                        className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md">
                        {p.label}
                      </button>
                    ))}
                  </div>
                  <div className="p-2 flex flex-col">
                    <CalendarComponent mode="range" selected={pendingDate}
                      onSelect={(r) => setPendingDate({ from: r?.from || null, to: r?.to || null })}
                      numberOfMonths={2} locale={es} />
                    <div className="border-t pt-2 flex justify-end">
                      <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white"
                        disabled={!pendingDate.from}
                        onClick={() => { const r = { from: pendingDate.from, to: pendingDate.to || pendingDate.from }; setDateRange(r); setCalOpen(false); }}>
                        Aplicar
                      </Button>
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            <div className="relative w-full lg:w-96">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={search}
                onChange={(e) => { setPagination((p) => ({ ...p, page: 1 })); setSearch(e.target.value); }}
                placeholder="Buscar referencia o nombre..."
                className="pl-9" data-testid="gads-products-search" />
            </div>
          </CardContent>
        </Card>

        {/* Feed Optimizer button — always visible, independent of date range */}
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={() => setDrawerOpen(true)}
            data-testid="feed-optimizer-button"
            className="shrink-0 border-slate-300 text-slate-700 hover:bg-slate-50 gap-1.5">
            <Settings2 className="w-4 h-4" />
            Feed Optimizer
          </Button>
        </div>

        {!hasRange ? (
          <Card className="bg-amber-50 border border-amber-200">
            <CardContent className="p-6">
              <p className="text-amber-900 font-semibold">Selecciona un rango de fechas para ver los datos</p>
            </CardContent>
          </Card>
        ) : noUtmData ? (
          /* ── UTM banner ── */
          <Card className="bg-amber-50 border border-amber-200" data-testid="no-utm-banner">
            <CardContent className="p-6 flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-amber-900">No se detectan UTMs de Google en tus pedidos</p>
                <p className="text-sm text-amber-800 mt-1">
                  Para activar esta tabla necesitas configurar el tracking UTM en tu tienda.
                  Asegúrate de que los pedidos lleguen con <code className="bg-amber-100 px-1 rounded text-xs">utm_source=google</code> en los eventos de tracking.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-white border border-slate-200">
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <CardTitle className="text-lg font-bold text-slate-900">Atribución por Producto — Google Ads</CardTitle>
                    {dataSource === "pixel" && (
                      <span
                        data-testid="data-source-badge-pixel"
                        className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                        Datos via Píxel ProfitSystem
                      </span>
                    )}
                    {dataSource === "ga4" && (
                      <span
                        data-testid="data-source-badge-ga4"
                        className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 border border-purple-200"
                        title="No se detectaron UTMs de Google en pedidos. Datos obtenidos desde GA4 Data API (itemId × sessionSource=google / sessionMedium=cpc)"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                        Datos via GA4
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500">
                    {loading ? "Cargando..." : `${pagination.total_rows} productos`}
                  </p>
                  <p className="text-xs text-slate-400">
                    {dataSource === "ga4"
                      ? "Ingresos desde GA4 Data API (google/cpc) · Gasto desde Google Ads API · Sin Halo Score (requiere UTM pixel)"
                      : "Ingresos y costes calculados desde pedidos reales con UTM Google · Gasto desde Google Ads API"}
                  </p>
                </div>
                <div>{/* spacer */}</div>
              </div>

              {/* Filter chips */}
              <div className="flex flex-wrap gap-2 mt-3" data-testid="decision-filter-chips">
                {FILTER_CHIPS.map((chip) => {
                  const active = activeFilter === chip.key;
                  const cnt = chip.key === "all" ? enrichedRows.length : (counts[chip.key] ?? 0);
                  return (
                    <button key={chip.key} type="button" onClick={() => setActiveFilter(chip.key)}
                      data-testid={`filter-chip-${chip.key}`}
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-all
                        ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
                      <span className={`w-2 h-2 rounded-full ${chip.dot}`} />
                      {chip.label}
                      <span className={`ml-0.5 ${active ? "text-slate-300" : "text-slate-400"}`}>{cnt}</span>
                    </button>
                  );
                })}
              </div>
            </CardHeader>

            <CardContent>
              <div className="overflow-x-auto" data-testid="gads-products-table-wrapper">
                <Table data-testid="gads-products-table">
                  <TableHeader>
                    <TableRow>
                      {COLUMNS.map((col) => (
                        <TableHead key={col.key}
                          className={`${col.align === "left" ? "text-left" : col.align === "center" ? "text-center" : "text-right"}
                            ${col.key === "_rent_real" ? "bg-slate-50 border-l border-slate-200" : ""}
                            ${col.key === "_decision"  ? "bg-slate-50 text-center" : ""}`}>
                          {col.special ? (
                            <span className="font-bold text-slate-800 text-xs">{col.label}</span>
                          ) : (
                            <button type="button" onClick={() => col.sortable && onSort(col.key)}
                              className={`inline-flex items-center gap-1 text-slate-600 hover:text-slate-900
                                ${col.align === "left" ? "" : "justify-end w-full"}`}>
                              {col.label}
                              {col.sortable && <SortIcon k={col.key} />}
                            </button>
                          )}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!loading && filteredRows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={COLUMNS.length} className="text-center text-slate-500 py-10">
                          {activeFilter !== "all" ? "No hay productos con esta clasificación." : "Sin datos para este rango."}
                        </TableCell>
                      </TableRow>
                    )}
                    {filteredRows.map((row, i) => {
                      const d = row._decision;
                      return (
                        <TableRow key={row.product_uid || i} data-testid={`gads-row-${i}`}>
                          {/* Referencia */}
                          <TableCell className="font-semibold text-slate-900">{row.reference || "—"}</TableCell>
                          {/* Nombre */}
                          <TableCell className="text-slate-700 max-w-[200px] truncate">{row.name || "—"}</TableCell>
                          {/* Conv. Directas */}
                          <TableCell className="text-right font-mono text-slate-700">
                            {row.direct_conversions > 0 ? fmtInt(row.direct_conversions) : "—"}
                          </TableCell>
                          {/* Ingresos Directos */}
                          <TableCell className={`text-right font-mono font-bold ${isNeg(row.direct_revenue) ? "text-red-600" : "text-blue-700"}`}>
                            {row.direct_revenue > 0 ? fmtEur(row.direct_revenue) : "—"}
                          </TableCell>
                          {/* Coste Directo */}
                          <TableCell className="text-right font-mono text-slate-700">
                            {row.direct_cost > 0 ? fmtEur(row.direct_cost) : "—"}
                          </TableCell>
                          {/* Gasto GAds */}
                          <TableCell className="text-right font-mono text-slate-700">{fmtEur(row.gads_spend)}</TableCell>
                          {/* ROAS GAds */}
                          <TableCell className={`text-right font-mono ${row.roas_gads != null && row.roas_gads >= 2 ? "text-green-700 font-semibold" : row.roas_gads != null && row.roas_gads < 1 ? "text-red-600" : "text-slate-700"}`}>
                            {fmtX(row.roas_gads)}
                          </TableCell>
                          {/* Rentabilidad GAds % */}
                          <TableCell className={`text-right font-mono ${isNeg(row.rent_gads_pct) ? "text-red-600 font-semibold" : (row.rent_gads_pct || 0) >= 20 ? "text-green-700 font-semibold" : "text-slate-700"}`}>
                            {fmtPct(row.rent_gads_pct)}
                          </TableCell>
                          {/* Ing. Cruzados */}
                          <TableCell className="text-right font-mono text-slate-500 text-sm">
                            {row.cross_revenue > 0 ? fmtEur(row.cross_revenue) : "—"}
                          </TableCell>
                          {/* Halo Score */}
                          <TableCell className={`text-right font-mono ${row.halo_score != null && row.halo_score > 1 ? "text-blue-600 font-semibold" : "text-slate-500"}`}>
                            {fmtX(row.halo_score)}
                          </TableCell>
                          {/* Rentabilidad Real % */}
                          <TableCell className="text-right bg-slate-50 border-l border-slate-100" data-testid={`gads-row-${i}-rent-real`}>
                            {row.rent_real_pct != null ? (
                              <span className={`font-mono font-semibold ${row.rent_real_pct >= 20 ? "text-green-700" : row.rent_real_pct >= 0 ? "text-amber-600" : "text-red-600"}`}>
                                {fmtPct(row.rent_real_pct)}
                              </span>
                            ) : <span className="text-slate-400 font-mono">—</span>}
                          </TableCell>
                          {/* Decisión */}
                          <TableCell className="text-center bg-slate-50" data-testid={`gads-row-${i}-decision`}>
                            <DecisionChip d={d} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {pagination.total_rows > 50 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-slate-500">Página {pagination.page} de {pagination.total_pages}</p>
                  <div className="flex gap-2">
                    <Button variant="outline" disabled={!canPrev || loading}
                      onClick={() => { const p = pagination.page - 1; setPagination((pr) => ({ ...pr, page: p })); fetchData(p); }}>
                      Anterior
                    </Button>
                    <Button variant="outline" disabled={!canNext || loading}
                      onClick={() => { const p = pagination.page + 1; setPagination((pr) => ({ ...pr, page: p })); fetchData(p); }}>
                      Siguiente
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Feed Optimizer Drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="feed-optimizer-drawer">
          <SheetHeader className="mb-6">
            <SheetTitle className="flex items-center gap-2 text-slate-900">
              <Settings2 className="w-5 h-5" /> Feed Optimizer
            </SheetTitle>
            <p className="text-sm text-slate-500">
              Feed suplementario para Google Merchant Center. La clasificación usa la Rentabilidad Real (basada en pedidos reales con UTM Google).
            </p>
          </SheetHeader>

          <div className="space-y-8">
            {/* URL del Feed */}
            <section data-testid="feed-optimizer-section-url">
              <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-3">URL del Feed</h3>
              {feedUrl ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-xs bg-slate-100 border border-slate-200 rounded px-3 py-2 text-slate-700 break-all" data-testid="feed-url-display">
                      {feedUrl}
                    </code>
                    <Button size="sm" variant="outline" onClick={handleCopy} data-testid="copy-feed-url-btn" className="shrink-0 gap-1">
                      {feedCopied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                      {feedCopied ? "Copiado" : "Copiar"}
                    </Button>
                  </div>
                  <div className="rounded bg-blue-50 border border-blue-200 p-3 text-xs text-blue-800">
                    Copia esta URL en <strong>Google Merchant Center → Productos → Feeds → Nuevo feed suplementario → URL programada</strong>.
                  </div>
                </div>
              ) : <p className="text-sm text-slate-400">Generando token...</p>}
            </section>

            {/* Umbrales */}
            <section data-testid="feed-optimizer-section-settings">
              <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-3">Configuración de umbrales</h3>
              <div className="space-y-5">
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Rentabilidad Real</p>
                  <div className="grid grid-cols-2 gap-3">
                    {[["umbral_escalar","Escalar si >"],["umbral_mantener","Mantener si >"]].map(([key, lbl]) => (
                      <div key={key} className="space-y-1">
                        <Label className="text-xs text-slate-600">{lbl}</Label>
                        <div className="flex items-center gap-1">
                          <Input type="number" min="0" step="1" value={drawerSettings[key]}
                            onChange={(e) => setDrawerSettings((s) => ({ ...s, [key]: parseFloat(e.target.value) || 0 }))}
                            className="font-mono text-sm" data-testid={`setting-${key}`} />
                          <span className="text-slate-400 text-sm">%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Protección Halo</p>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-600">Proteger si Halo Score &gt;</Label>
                    <div className="flex items-center gap-1 max-w-[160px]">
                      <Input type="number" min="0" step="0.1" value={drawerSettings.umbral_halo_proteger}
                        onChange={(e) => setDrawerSettings((s) => ({ ...s, umbral_halo_proteger: parseFloat(e.target.value) || 0 }))}
                        className="font-mono text-sm" data-testid="setting-umbral-halo" />
                      <span className="text-slate-400 text-sm">x</span>
                    </div>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Filtro de entrada</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-600">Gasto mínimo para clasificar (% del precio)</Label>
                      <div className="flex items-center gap-1">
                        <Input type="number" min="0" step="1" value={Math.round((drawerSettings.factor_minimo || 0.2) * 100)}
                          onChange={(e) => setDrawerSettings((s) => ({ ...s, factor_minimo: (parseFloat(e.target.value) || 0) / 100 }))}
                          className="font-mono text-sm" data-testid="setting-factor-minimo" />
                        <span className="text-slate-400 text-sm">%</span>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-600">Ventana de cálculo (días)</Label>
                      <div className="flex items-center gap-1">
                        <Input type="number" min="1" max="365" step="1" value={drawerSettings.dias_lookback || 30}
                          onChange={(e) => setDrawerSettings((s) => ({ ...s, dias_lookback: parseInt(e.target.value) || 30 }))}
                          className="font-mono text-sm" data-testid="setting-dias-lookback" />
                        <span className="text-slate-400 text-sm">días</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Regeneración automática</p>
                  <div className="space-y-1 max-w-[200px]">
                    <Label className="text-xs text-slate-600">Hora del día (España)</Label>
                    <select
                      value={drawerSettings.hora_regeneracion ?? 6}
                      onChange={(e) => setDrawerSettings((s) => ({ ...s, hora_regeneracion: parseInt(e.target.value) }))}
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm font-mono"
                      data-testid="setting-hora-regeneracion"
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>{String(i).padStart(2, "0")}:00</option>
                      ))}
                    </select>
                    <p className="text-[10px] text-slate-400">El feed se regenera automáticamente 1 vez al día a esta hora</p>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Segmentos de margen de catálogo</p>
                  <div className="grid grid-cols-2 gap-3">
                    {[["umbral_margen_alto","Margen alto si >"],["umbral_margen_medio","Margen medio si >"]].map(([key, lbl]) => (
                      <div key={key} className="space-y-1">
                        <Label className="text-xs text-slate-600">{lbl}</Label>
                        <div className="flex items-center gap-1">
                          <Input type="number" min="0" step="1" value={drawerSettings[key]}
                            onChange={(e) => setDrawerSettings((s) => ({ ...s, [key]: parseFloat(e.target.value) || 0 }))}
                            className="font-mono text-sm" data-testid={`setting-${key}`} />
                          <span className="text-slate-400 text-sm">%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1">Basado en precio de venta vs coste del producto (catálogo)</p>
                </div>
                <Button onClick={handleSave} disabled={drawerSaving}
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid="save-feed-settings-btn">
                  {drawerSaving ? "Guardando..." : "Guardar configuración"}
                </Button>
              </div>
            </section>

            {/* Preview del Feed */}
            <section data-testid="feed-optimizer-section-preview">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Preview del Feed</h3>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={handleRegenerate} disabled={feedGenerating}
                    className="gap-1 text-xs" data-testid="regenerate-csv-btn">
                    <RefreshCw className={`w-3.5 h-3.5 ${feedGenerating ? "animate-spin" : ""}`} />
                    {feedGenerating ? "Generando..." : "Regenerar"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleDownload} disabled={!feedUrl || feedGenerating}
                    className="gap-1 text-xs" data-testid="download-csv-btn">
                    <Download className="w-3.5 h-3.5" />
                    {feedGenerating ? "Preparando..." : "Descargar CSV"}
                  </Button>
                </div>
              </div>
              {feedStats && (
                <div className="flex flex-wrap gap-3 mb-3 text-xs" data-testid="feed-stats-summary">
                  <span className="text-slate-700 font-semibold">{feedStats.total_products} productos en el feed</span>
                  <span className="text-green-700">{feedStats.classified} clasificados</span>
                  {feedStats.datos_insuficientes > 0 && (
                    <span className="text-amber-700 flex items-center gap-1 cursor-help" data-testid="feed-insufficient-count"
                      title={`Estos ${feedStats.datos_insuficientes} productos tienen gasto publicitario pero no alcanzan el mínimo configurado (${Math.round((feedStats.factor_minimo || 0.2) * 100)}% del precio unitario) en los últimos ${feedStats.dias_lookback || 30} días.`}>
                      <Info className="w-3 h-3" />
                      {feedStats.datos_insuficientes} datos insuficientes
                    </span>
                  )}
                  <span className="text-slate-500">{feedStats.sin_publicidad} sin publicidad</span>
                </div>
              )}
              {enrichedRows.length > 0 ? (
                <>
                  <div className="flex flex-wrap gap-2 mb-3 text-xs" data-testid="feed-preview-summary">
                    <span className="text-green-700">{counts.escalar} escalar</span>
                    <span className="text-blue-700">{counts.mantener} mantener</span>
                    <span className="text-orange-700">{counts.reducir_bid} reducir</span>
                    <span className="text-red-700">{counts.pausar} pausar</span>
                  </div>
                  <div className="overflow-x-auto rounded border border-slate-200">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left px-3 py-2 font-semibold text-slate-600">Nombre</th>
                          <th className="text-right px-3 py-2 font-semibold text-slate-600">Rent. Real</th>
                          <th className="text-right px-3 py-2 font-semibold text-slate-600">Halo</th>
                          <th className="text-center px-3 py-2 font-semibold text-slate-600">Decisión</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewRows.map((row, i) => (
                          <tr key={i} className="border-t border-slate-100">
                            <td className="px-3 py-1.5 text-slate-700 max-w-[140px] truncate">{row.name || row.reference || "—"}</td>
                            <td className="px-3 py-1.5 text-right font-mono text-slate-600">
                              {row.rent_real_pct != null ? `${Number(row.rent_real_pct).toFixed(1)}%` : "—"}
                            </td>
                            <td className="px-3 py-1.5 text-right font-mono text-slate-500">{fmtX(row.halo_score)}</td>
                            <td className="px-3 py-1.5 text-center"><DecisionChip d={row._decision} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <p className="text-sm text-slate-400 text-center py-4">Selecciona un rango de fechas para ver el preview.</p>
              )}
            </section>
          </div>
        </SheetContent>
      </Sheet>
    </DashboardLayout>
  );
}
